import { useParams } from 'react-router';
import { Link } from 'react-router-dom';
import { useApolloClient } from '@apollo/client';
import { ExternalLinkIcon } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useBreadcrumbs } from '@/components/Breadcrumbs/useBreadcrumbs';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import {
  usePickingJobDetailQuery,
  type PickingJobDetailQuery,
} from '@/graphql/queries/pickingJobDetail.generated';
import { PickingJobPickedItemsDocument } from '@/graphql/queries/pickingJobPickedItems.generated';
import { PickingJobStatus } from '@/graphql/generatedTypes';
import { Routes } from '@/utils/routes';
import i18n from '@/utils/i18n';
import { getStatusTranslation } from '@/views/Order/utils/orderListFilters';
import { PickedItemsCard } from './PickedItemsCard';
import { PickingJobSidebar } from './PickingJobSidebar';

export function PickingJobDetail() {
  const { pickingJobId } = useParams<{ pickingJobId: string }>();

  const client = useApolloClient();
  const { data, loading, error, refetch } = usePickingJobDetailQuery({
    variables: { picking_job_id: pickingJobId },
    fetchPolicy: 'cache-and-network',
    skip: !pickingJobId,
  });

  const job = data?.getPickingJob;

  useDocumentTitle(i18n.t('Picking:detailPageTitle'));
  useBreadcrumbs({
    id: 'picking-job-detail',
    items: [
      {
        label: job ? job.id.slice(0, 8) : i18n.t('Picking:detailPageTitle'),
        href: Routes.pickingJobDetails(pickingJobId),
      },
    ],
  });

  if (loading && !job) {
    return (
      <div className="tw:flex tw:flex-col tw:gap-4">
        <Skeleton className="tw:h-10 tw:w-64" />
        <Skeleton className="tw:h-64 tw:w-full" />
      </div>
    );
  }

  if (error || !job) {
    return (
      <Alert variant="destructive">
        <AlertTitle>{i18n.t('Picking:detailNotFoundTitle')}</AlertTitle>
        <AlertDescription className="tw:text-wrap">
          <p className="tw:break-all">
            {error?.message ?? i18n.t('Picking:detailNotFoundBody')}
          </p>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="tw:flex tw:flex-col tw:gap-6">
      <div className="tw:flex tw:flex-col tw:gap-2">
        <h1 className="tw:text-4xl tw:font-semibold">
          {i18n.t('Picking:detailPageTitle')}
        </h1>
        <p className="tw:leading-6">
          {i18n.t('Picking:detailPageDescription')}
        </p>
      </div>

      <div className="tw:flex tw:items-start tw:gap-4">
        <div className="tw:flex-1 tw:flex tw:flex-col tw:gap-4 tw:min-w-0">
          <OrderedVsPickedCard items={job.items} status={job.status} />
          <PickedItemsCard key={job.id} pickingJobId={job.id} />
          <LinkedOrdersCard orders={job.ops_orders} />
        </div>

        <PickingJobSidebar
          job={job}
          onActionSettled={() => {
            // The job query and the picked-items query are separate; a
            // resolution changes both (status here, the booked and delivered
            // marks there), so both are refreshed. After a refusal too: it
            // may mean someone else changed the job meanwhile, and the
            // action should say so.
            refetch().catch(() => null);
            client
              .refetchQueries({ include: [PickingJobPickedItemsDocument] })
              .catch(() => null);
          }}
        />
      </div>
    </div>
  );
}

type PickingJob = NonNullable<PickingJobDetailQuery['getPickingJob']>;

function OrderedVsPickedCard({
  items,
  status,
}: {
  items: PickingJob['items'];
  status: PickingJobStatus;
}) {
  return (
    <Card>
      <CardContent className="tw:flex tw:flex-col tw:gap-4">
        <h2 className="tw:text-lg tw:font-semibold">
          {i18n.t('Picking:detailOrderedVsPicked')}
        </h2>
        {/* The picked rows are kept on a released job on purpose — they are the
            record of what was on it. Without a word of explanation the table
            reads as though the stock were still out, which is the opposite of
            what a release means. */}
        {status === PickingJobStatus.Released && (
          <Alert>
            <AlertDescription>
              {i18n.t('Picking:detailReleasedPickedNotice')}
            </AlertDescription>
          </Alert>
        )}
        <div className="tw:rounded-md tw:border tw:overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{i18n.t('Picking:detailColumnItemType')}</TableHead>
                <TableHead>{i18n.t('Picking:detailColumnOrdered')}</TableHead>
                <TableHead>{i18n.t('Picking:detailColumnPicked')}</TableHead>
                <TableHead>{i18n.t('Picking:detailColumnDelivered')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    className="tw:h-24 tw:text-center tw:text-muted-foreground"
                  >
                    {i18n.t('Common:noResults')}
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item) => {
                  const short = item.amount_picked < item.amount_ordered;
                  return (
                    <TableRow key={item.id}>
                      <TableCell>{item.type_name}</TableCell>
                      <TableCell>{item.amount_ordered}</TableCell>
                      <TableCell>
                        <span
                          className={
                            short || item.is_over_picked
                              ? 'tw:font-medium tw:text-destructive'
                              : undefined
                          }
                        >
                          {item.amount_picked}
                        </span>
                      </TableCell>
                      <TableCell>{item.amount_delivered}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

function LinkedOrdersCard({ orders }: { orders: PickingJob['ops_orders'] }) {
  return (
    <Card>
      <CardContent className="tw:flex tw:flex-col tw:gap-4">
        <h2 className="tw:text-lg tw:font-semibold">
          {i18n.t('Picking:detailLinkedOrders')}
        </h2>
        {orders.length === 0 ? (
          <p className="tw:text-sm tw:text-muted-foreground">
            {i18n.t('Common:noResults')}
          </p>
        ) : (
          <div className="tw:flex tw:flex-col tw:gap-2">
            {orders.map((order) => (
              <div
                key={order.id}
                className="tw:flex tw:flex-wrap tw:items-center tw:gap-3"
              >
                <Badge variant="secondary">
                  {getStatusTranslation(order.status)}
                </Badge>
                <Button variant="outline" size="sm" asChild>
                  <Link to={Routes.orderDetails(order.id)}>
                    <ExternalLinkIcon className="tw:size-4 tw:shrink-0 tw:text-muted-foreground" />
                    <span className="tw:truncate">{order.id.slice(0, 8)}</span>
                  </Link>
                </Button>
                {order.ops_comment && (
                  <span className="tw:text-sm tw:text-muted-foreground tw:truncate">
                    {order.ops_comment}
                  </span>
                )}
                {/* The order detail page links the job that owns it now. */}
                {!order.owned_by_job && (
                  <span className="tw:text-sm tw:text-muted-foreground">
                    {i18n.t('Picking:detailOrderOnOtherJob')}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
